import { Fragment, useState } from 'react'

import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded'
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid2,
  Typography
} from '@mui/material'
import Currency from 'components/Currency'
import DateTime from 'components/DateTime'
import BuyIcon from 'components/icons/Buy'
import BuyVoucherIcon from 'components/icons/BuyVoucher'
import DepositIcon from 'components/icons/Deposit'
import PoliDepositIcon from 'components/icons/PoliDeposit'
import BlinkpayDepositIcon from 'components/icons/BlinkpayDeposit'
import SellIcon from 'components/icons/Sell'
import useStyles from './styles'
import InsuranceFeeIcon from 'components/icons/InsuranceFee'
import WithdrawalIcon from 'components/icons/Withdrawal'
import CreditCardIcon from 'components/icons/CreditCard/CreditCard'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import { generateUsername } from 'utils/util'
import { useAuth } from 'components/AuthProvider'

interface LedgerEntry {
  createdAt: string
  credit: number
  debit: number
  source: {
    sourceCategorisation: string
    sourceInfo: string
    sourceNote?: string | null
  }
  __typename?: string
}

export interface Vault {
  dateOfBirth: string
  email: string
  id: string
  name: string
  relationship: string
  balance: string
  securitiesOwned: [SecuritiesOwned]
}

export interface SecuritiesOwned {
  quantityOwned: number
  hasHistory: boolean
  securities: [Security]
  product: Product
}

export interface Security {
  quantityOwned: number
}

export interface Product {
  name: string
  id: string
}

// TODO: See if we can utilise existing graphql types
export interface ActivityItemProps {
  activity?: {
    id: number
    createdAt: string
    entries: LedgerEntry[]
    reference: string
    total: string
  }
  vault?: Partial<Vault>
}

enum ActivityTitle {
  AccountFee = 'Account fee',
  Buy = 'Buy',
  BuyVoucher = 'Buy Voucher',
  CreditInvalidation = 'Credit invalidation',
  Deposit = 'Deposit',
  Fee = 'Fee',
  InsuranceFee = 'Insurance fee',
  OneOffPromotion = 'One-off promotion',
  PoliDeposit = 'Poli Deposit',
  PoliDepositFee = 'Poli Deposit Fee',
  BlinkpayDeposit = 'BlinkPay Deposit',
  BlinkpayDepositPending = 'BlinkPay Deposit (Pending)',
  BlinkpayDepositFee = 'BlinkPay Deposit Fee',
  BlinkpayDepositReversal = 'BlinkPay Deposit Reversal',
  ReceiveVoucher = 'Receive voucher',
  Reversal = 'Reversal',
  Reward = 'Reward',
  RewardForSignUp = 'Reward for Sign Up',
  RewardFromReferral = 'Reward From Referral',
  TopUp = 'Top-up with credit card',
  Sell = 'Sell',
  TransactionFee = 'Transaction fee',
  Withdrawal = 'Withdrawal'
}

export default function ActivityItem({
  activity,
  vault
}: ActivityItemProps): JSX.Element | null {
  const { classes } = useStyles()

  const { user } = useAuth()

  const [expanded, setExpanded] = useState<boolean>(false)
  const sourceCategorisation = activity?.entries[0]?.source.sourceCategorisation

  const handleToggleExpanded = () => {
    setExpanded(!expanded)
  }

  const renderIcon = () => {
    const title = activity?.entries[0].source
      .sourceCategorisation as ActivityTitle

    switch (title) {
      case ActivityTitle.AccountFee:
      case ActivityTitle.Fee:
      case ActivityTitle.TransactionFee:
        return (
          <Box
            width={40}
            height={40}
            display='flex'
            justifyContent='center'
            alignItems='center'
            borderRadius='50%'
            bgcolor='#26BFEF'
          >
            <PaidOutlinedIcon color='white' width={40} height={40} />
          </Box>
        )
      case ActivityTitle.Buy:
        return <BuyIcon />
      case ActivityTitle.BuyVoucher:
      case ActivityTitle.ReceiveVoucher:
        return <BuyVoucherIcon />
      case ActivityTitle.Sell:
        return <SellIcon />
      case ActivityTitle.PoliDeposit:
      case ActivityTitle.PoliDepositFee:
        return <PoliDepositIcon />
      case ActivityTitle.BlinkpayDeposit:
      case ActivityTitle.BlinkpayDepositPending:
      case ActivityTitle.BlinkpayDepositFee:
      case ActivityTitle.BlinkpayDepositReversal:
        return <BlinkpayDepositIcon />
      case ActivityTitle.Deposit:
        return <DepositIcon />
      case ActivityTitle.InsuranceFee:
        return <InsuranceFeeIcon />
      case ActivityTitle.Withdrawal:
      case ActivityTitle.Reversal:
        return <WithdrawalIcon />
      case ActivityTitle.TopUp:
      case ActivityTitle.CreditInvalidation:
        return <CreditCardIcon />
      default:
        if (/reward/i.test(title) || /promotion/i.test(title)) {
          return (
            <Box
              width={40}
              height={40}
              display='flex'
              justifyContent='center'
              alignItems='center'
              borderRadius='50%'
              border='1px solid black'
            >
              <EmojiEventsIcon width={40} height={40} />
            </Box>
          )
        }

        return <></>
    }
  }

  const renderEntries = (entries: LedgerEntry[]): JSX.Element | null => {
    // Only render entries if we have more than one LedgerEntry associated to the Activity (i.e. it doesn't make sense to show "Deposit" twice)
    return entries.length > 0 ? (
      <>
        {entries.map(entry => {
          // TODO: Expose a better key for each entry
          return (
            <Fragment
              key={`${activity?.reference}-${entry.createdAt}-${entry.source.sourceCategorisation}`}
            >
              <Grid2 size={{ xs: 6 }}>
                <Typography component='p' fontSize='18px'>
                  {entry.__typename === 'ExpirableWalletCreditEntry'
                    ? 'Special funds'
                    : entry.source.sourceCategorisation}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 6 }}>
                <Typography
                  component='p'
                  fontSize='18px'
                  fontWeight='700'
                  textAlign='right'
                  textTransform='uppercase'
                >
                  <Currency
                    amount={Math.abs(Number(entry.credit - entry.debit))}
                  />
                </Typography>
              </Grid2>
              {entry.source.sourceNote && (
                <>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography component='p' fontSize='18px'>
                      Note
                    </Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography component='p' fontSize='18px' align='right'>
                      {entry.source.sourceNote}
                    </Typography>
                  </Grid2>
                </>
              )}
            </Fragment>
          )
        })}
      </>
    ) : null
  }

  const buildExpandIconClasses = (): string => {
    let className = classes.expandIcon

    if (expanded) {
      className += ` ${classes.expandIconExpanded}`
    }

    return className
  }

  return activity ? (
    <Accordion
      className={classes.accordion}
      expanded={expanded}
      onChange={handleToggleExpanded}
    >
      <AccordionSummary
        aria-controls={`${activity.reference}-content`}
        className={classes.accordionSummary}
        id={`${activity.reference}-header`}
      >
        <Grid2 className={classes.activities} size={{ xs: 12 }}>
          {renderIcon()}
          <Box flex={1}>
            <Box display='flex' alignItems='center' gap='6px'>
              <Typography fontSize='18px' color='#1E1E1E'>
                <Currency amount={Math.abs(Number(activity.total))} />
              </Typography>
              {sourceCategorisation ===
                ActivityTitle.BlinkpayDepositPending && (
                <Typography fontSize='12px' color='#757575'>
                  (Pending)
                </Typography>
              )}
            </Box>
            <Typography fontSize='12px' color='#757575'>
              <DateTime date={activity.createdAt} format='DD MMMM YYYY' />
            </Typography>
          </Box>
          <ArrowDropDownRoundedIcon className={buildExpandIconClasses()} />
        </Grid2>
      </AccordionSummary>
      <AccordionDetails>
        <Grid2 container spacing={0}>
          {!!vault && (
            <>
              <Grid2 size={{ xs: 6 }}>
                <Typography component='p' fontSize='18px'>
                  Vault
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 6 }}>
                <Typography
                  component='p'
                  fontSize='18px'
                  fontWeight='700'
                  textAlign='right'
                >
                  {vault.name ?? generateUsername(user)}
                </Typography>
              </Grid2>
            </>
          )}

          <Grid2 size={{ xs: 6 }}>
            <Typography component='p' fontSize='18px'>
              Reference
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <Typography
              component='p'
              fontSize='18px'
              fontWeight='700'
              textAlign='right'
              textTransform='uppercase'
            >
              {activity.reference}
            </Typography>
          </Grid2>

          {renderEntries(activity.entries)}
        </Grid2>
      </AccordionDetails>
    </Accordion>
  ) : null
}
